// A card's rules, as crisp text for its close-up: its stats, its descriptor lines and its description (or an
// Emperor's abilities), with every status effect in a box of its token's color.
// The same things are printed on the placeholder card itself (images.ts), where space is tight; here they
// are always easy to read.
import { CONFIG } from '../config.ts';
import { type CardDef, STAT_NAMES, cardStats, titleCase } from '../shared/cards.ts';
import { abilityTerms, statusPieces } from './cardText.ts';
import { statColor, tokenColor } from './images.ts';
import { el } from './ui.ts';

function isLight(hex: string) {
  const n = Number.parseInt(hex.replace('#', ''), 16);
  return (0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255 > 0.62;
}

/** A description as nodes: plain words, and status effects in colored boxes. A leading "Entrance:" is bold. */
function richText(text: string): (Node | string)[] {
  const out: (Node | string)[] = [];
  const label = text.match(/^([A-Za-z]+):/);
  let rest = text;
  if (label && CONFIG.cardText.labels.includes(label[1])) {
    out.push(el('strong', {}, `${label[1]}:`));
    rest = text.slice(label[0].length);
  }
  for (const piece of statusPieces(rest)) {
    if (!piece.token) out.push(piece.text);
    else {
      const color = tokenColor(piece.token);
      out.push(el('span', { class: `status-word ${isLight(color) ? 'on-light' : ''}`, style: `--badge: ${color}`, title: piece.token.name }, piece.text));
    }
  }
  return out;
}

/** The block shown beside a card's close-up. null if the card has nothing to say (no stats, lines or text). */
export function cardRulesNode(def: CardDef | undefined): HTMLElement | null {
  if (!def) return null;
  const stats = cardStats(def);
  const paragraphs = def.text.split('\n').map((p) => p.trim()).filter(Boolean);
  if (!stats.length && !def.traits.length && !paragraphs.length && !def.emperorAbilities.length) return null;
  return el(
    'div',
    { class: 'card-rules' },
    stats.length
      ? el(
          'div',
          { class: 'card-rules-stats' },
          stats.map((st) =>
            el('span', { class: 'card-stat', style: `--stat: ${statColor(st.icon)}` }, el('span', { class: 'card-stat-name' }, STAT_NAMES[st.icon]), el('span', { class: 'card-stat-value' }, st.dark ? `${st.value} Dark Gerzite` : st.value)),
          ),
        )
      : null,
    def.traits.length ? el('div', { class: 'card-rules-traits' }, [titleCase(def.types.join(' / ')), ...def.traits].join(' · ')) : null,
    paragraphs.length ? el('div', { class: 'card-rules-text' }, paragraphs.map((p) => el('p', {}, ...richText(p)))) : null,
    def.emperorAbilities.length
      ? el(
          'div',
          { class: 'card-rules-text' },
          def.emperorAbilities.map((ab) => el('div', { class: 'card-rules-ability' }, el('div', { class: 'card-rules-ability-name' }, el('strong', {}, ab.name), el('span', {}, abilityTerms(ab))), el('p', {}, ...richText(ab.text)))),
        )
      : null,
  );
}
